import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';
import { Button } from '@/components/ui/button';
import {
  Users,
  MoreHorizontal,
  Star,
  ShieldCheck,
  Smartphone,
  ChartLine,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  DataTable,
  DataTableContainer,
  DataTableContent,
  DataTableHeader,
  DataTableRows,
  DataTableWrapper,
  DataTableFooter,
  DataTablePageHeader,
  DataTableUtilityBar,
} from '@/components/data-table';
import { useUsers, useDeleteUser } from './hooks/useUsers';
import { useDebounce } from '@/hooks/use-debounce';
import { AdminUserSearchDto, Roles, ROLE_DISPLAY, CATEGORY_CONFIG, RoleCategory } from './userTypes';

const CATEGORY_ICONS: Record<RoleCategory, LucideIcon> = {
  monitoring: ChartLine,
  admin: ShieldCheck,
  watchfulGo: Smartphone,
};

const ROLE_OVERRIDES: Partial<Record<Roles, { icon: LucideIcon; bg: string; text: string }>> = {
  [Roles.SuperAdmin]: { icon: Star, bg: 'bg-yellow-500/10', text: 'text-yellow-400' },
};

interface UserTableData {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  rolesDisplay: React.ReactNode;
}

const RoleBadge = ({ role }: { role: string }) => {
  const getRoleLabels = (rolesStr: string): { label: string; bg: string; text: string; icon: LucideIcon }[] => {
    try {
      const rolesArray: string[] = rolesStr.startsWith('[') ? JSON.parse(rolesStr) : [rolesStr];
      const labels: { label: string; bg: string; text: string; icon: LucideIcon }[] = [];

      for (const r of rolesArray) {
        const roleEnum = r as Roles;
        const display = ROLE_DISPLAY[roleEnum];
        if (display) {
          const override = ROLE_OVERRIDES[roleEnum];
          const colors = override ?? CATEGORY_CONFIG[display.category];
          const icon = override?.icon ?? CATEGORY_ICONS[display.category];
          labels.push({ label: display.label, bg: colors.bg, text: colors.text, icon });
        }
      }

      if (labels.length === 0) {
        labels.push({ label: 'No Access', bg: 'bg-gray-700/20', text: 'text-gray-400', icon: ChartLine });
      }

      return labels;
    } catch (e) {
      console.error('Error parsing roles:', e);
      return [{ label: 'No Access', bg: 'bg-gray-700/20', text: 'text-gray-400', icon: ChartLine }];
    }
  };

  const roleLabels = getRoleLabels(role);

  return (
    <div className="flex flex-wrap gap-2">
      {roleLabels.map((item) => {
        const Icon = item.icon;
        return (
          <div key={item.label} className={`flex items-center gap-1.5 rounded-md px-2 py-1 ${item.bg}`}>
            <Icon className={`h-3.5 w-3.5 ${item.text}`} />
            <span className={`text-sm font-medium ${item.text}`}>{item.label}</span>
          </div>
        );
      })}
    </div>
  );
};

function extractUserTableData(users: AdminUserSearchDto[]): UserTableData[] {
  return users.map((user) => ({
    id: user.id,
    name: `${user.firstName || ''} ${user.lastName || ''}`,
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    roles: user.roles || [],
    rolesDisplay: user.roles ? <RoleBadge role={JSON.stringify(user.roles)} /> : null,
  }));
}

export function UsersPage() {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useQueryStates(
    {
      page: parseAsInteger.withDefault(1),
      pageSize: parseAsInteger.withDefault(25),
      search: parseAsString,
    },
    { history: 'replace' }
  );
  const page = urlParams.page;
  const pageSize = urlParams.pageSize;
  const searchValue = urlParams.search || '';
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null);
  const debouncedSearchValue = useDebounce(searchValue, 300);
  const deleteUser = useDeleteUser();

  const { data, isLoading, error } = useUsers({
    pageNumber: page,
    pageSize,
    searchValue: debouncedSearchValue,
  });

  const userColumns: ColumnDef<UserTableData>[] = useMemo(() => [
    {
      id: 'name',
      accessorKey: 'name',
      enableHiding: false,
      meta: { label: 'User' },
      header: 'User',
      cell: ({ row }) => {
        const { name, id } = row.original;
        return (
          <a
            href={`/users/${id}`}
            className="font-semibold text-lime hover:underline"
          >
            {name}
          </a>
        );
      },
    },
    {
      id: 'email',
      accessorKey: 'email',
      meta: { label: 'Email' },
      header: 'Email',
      cell: ({ row }) => <span>{row.original.email}</span>,
    },
    {
      id: 'roles',
      meta: { label: 'Role' },
      header: 'Role',
      cell: ({ row }) => row.original.rolesDisplay,
    },
    {
      id: 'actions',
      enableHiding: false,
      meta: { label: 'Actions', align: 'right' },
      header: 'Actions',
      cell: ({ row }) => {
        const user = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/users/${user.id}`)}>
                Edit User
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-500"
                onClick={() => setUserToDelete({ id: user.id, name: user.name })}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ], [navigate]);

  const userData = useMemo(() => {
    if (!data?.rows) return [];
    return extractUserTableData(data.rows);
  }, [data?.rows]);

  return (
    <div className="relative flex h-full flex-col">
      <div data-onboarding="users-page">
        <DataTablePageHeader
          title="Users"
          icon={<Users className="h-6 w-6" />}
          actions={
            <Button
              onClick={() => navigate('/users/add')}
              data-onboarding="users-add-button"
            >
              Add User
            </Button>
          }
        />
      </div>
      <DataTable
        columns={userColumns}
        data={userData}
        loading={isLoading}
        error={error as Error | null}
        page={page}
        pageSize={pageSize}
        totalCount={data?.totalRows ?? 0}
        onPageChange={(p) => setUrlParams({ page: p })}
        onPageSizeChange={(ps) => setUrlParams({ pageSize: ps, page: 1 })}
        pageSizeOptions={[10, 25, 50, 100]}
        stateKey="users-table"
        emptyMessage="No users found"
      >
        <DataTableContainer>
          <DataTableContent>
            <div data-onboarding="users-utility-bar">
              <DataTableUtilityBar
                showFilterToggle={false}
                showColumnVisibility
                search={{
                  value: searchValue,
                  onChange: (value) => setUrlParams({ search: value || null, page: 1 }),
                  placeholder: 'Search users by name, email, or phone...',
                }}
              />
            </div>
            <div data-onboarding="users-table">
              <DataTableWrapper>
                <DataTableHeader />
                <DataTableRows />
              </DataTableWrapper>
            </div>
            <DataTableFooter />
          </DataTableContent>
        </DataTableContainer>
      </DataTable>

      <Dialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
        <DialogContent className="border-white/10 bg-card2">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {userToDelete?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!userToDelete) return;
                await deleteUser.mutateAsync(userToDelete.id);
                setUserToDelete(null);
              }}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}